"use client";

import React from "react";
import {
  Grid,
  Button,
  IconButton,
  Stack,
  Box,
  Typography,
  Paper,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useFormContext, Controller } from "react-hook-form";
import { BASE_URL } from "../../constants/url";

const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const isHttp = (s) => typeof s === "string" && /^https?:\/\//i.test(s);

export default function DocumentsSection() {
  const { control, setValue, watch } = useFormContext();

  const nicimgFile = watch("nicimg");
  const passportFile = watch("passportFile");
  const contractFile = watch("contractFile");

  const passportImgName = watch("passportimg");
  const contractPdfName = watch("contractpdf");
  const nicimgName = typeof nicimgFile === "string" ? nicimgFile : watch("nicimg");

  const validateFile = (file, acceptedTypes, maxSize = MAX_FILE_SIZE) => {
    if (!file) return null;
    
    if (file.size > maxSize) {
      return `File size must be less than ${maxSize / 1024 / 1024}MB`;
    }
    
    if (acceptedTypes && !acceptedTypes.includes(file.type)) {
      return `File type not supported. Please upload ${acceptedTypes.join(", ")}`;
    }
    
    return null;
  };

  const getPreviewUrl = (fileOrName, basePath = "") => {
    if (!fileOrName) return "";
    if (fileOrName instanceof File) return URL.createObjectURL(fileOrName);
    if (isHttp(fileOrName)) return fileOrName;
    return `${basePath}${fileOrName}`;
  };

  const nicPreview = getPreviewUrl(
    nicimgFile instanceof File ? nicimgFile : nicimgName,
    `${BASE_URL}AgentIDImages/`
  );
  const passportPreview = getPreviewUrl(
    passportFile instanceof File ? passportFile : passportImgName,
    `${BASE_URL}AgentPassport/`
  );
  const contractPreview = getPreviewUrl(
    contractFile instanceof File ? contractFile : contractPdfName,
    `${BASE_URL}Contracts/`
  );

  const handleFileUpload = (fieldName, file, acceptedTypes = null) => {
    const error = validateFile(file, acceptedTypes);
    if (error) {
      // You might want to set this error in form state
      console.error(`File validation error for ${fieldName}:`, error);
      return;
    }
    
    setValue(fieldName, file, { 
      shouldValidate: true, 
      shouldDirty: true 
    });
  };

  const handleRemove = (fieldName) => {
    setValue(fieldName, null, { 
      shouldValidate: true, 
      shouldDirty: true 
    });
  };

  const DocumentCard = ({ 
    previewUrl, 
    fileType, 
    onRemove, 
    fileName,
    uploadDate 
  }) => {
    const isImage = fileType === "image";
    const isPdf = fileType === "pdf";
    const hasFile = Boolean(previewUrl);

    return (
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: hasFile ? "primary.light" : "grey.300",
          backgroundColor: hasFile ? "background.paper" : "grey.50",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: hasFile ? 2 : 1,
          },
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* File Icon/Preview */}
          <Box sx={{ flexShrink: 0 }}>
            {isImage && hasFile ? (
              <Box
                component="img"
                src={previewUrl}
                alt="Document preview"
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "2px solid",
                  borderColor: "primary.light",
                }}
              />
            ) : isPdf && hasFile ? (
              <Box sx={{ textAlign: "center" }}>
                <PictureAsPdfIcon 
                  color="error" 
                  sx={{ fontSize: 48 }} 
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  PDF
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center" }}>
                <InsertDriveFileIcon 
                  color="disabled" 
                  sx={{ fontSize: 48 }} 
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  No file
                </Typography>
              </Box>
            )}
          </Box>

          {/* File Info */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {hasFile ? (
              <>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600}
                  noWrap
                  title={fileName}
                >
                  {fileName || "Uploaded file"}
                </Typography>
                {uploadDate && (
                  <Typography variant="caption" color="text.secondary">
                    Uploaded: {uploadDate}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No file uploaded
              </Typography>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            <IconButton
              color="primary"
              size="small"
              disabled={!hasFile}
              component="a"
              href={hasFile ? previewUrl : undefined}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                opacity: hasFile ? 1 : 0.5,
                "&:hover": { backgroundColor: "primary.light" },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="error"
              size="small"
              disabled={!hasFile}
              onClick={onRemove}
              sx={{
                opacity: hasFile ? 1 : 0.5,
                "&:hover": { backgroundColor: "error.light" },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    );
  };

  const UploadField = ({ 
    name, 
    label, 
    accept, 
    fileType, 
    previewUrl, 
    currentFile,
    validationRules 
  }) => (
    <Grid item xs={12} md={4}>
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ fieldState }) => (
          <Stack spacing={2}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
              color={fieldState.error ? "error" : "primary"}
              sx={{
                py: 1.5,
                borderStyle: "dashed",
                borderWidth: 2,
                backgroundColor: "background.paper",
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: "action.hover",
                },
              }}
            >
              {label}
              <input
                type="file"
                hidden
                accept={accept}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  handleFileUpload(name, file, fileType === "image" ? SUPPORTED_IMAGE_TYPES : null);
                }}
              />
            </Button>

            <DocumentCard
              previewUrl={previewUrl}
              fileType={fileType}
              fileName={currentFile?.name}
              onRemove={() => handleRemove(name)}
            />

            {fieldState.error && (
              <Alert severity="error" sx={{ fontSize: "0.75rem", py: 0 }}>
                {fieldState.error.message}
              </Alert>
            )}
            
            <Typography variant="caption" color="text.secondary">
              {fileType === "image" 
                ? "Supported formats: JPEG, PNG, GIF • Max size: 10MB"
                : "Format: PDF • Max size: 10MB"
              }
            </Typography>
          </Stack>
        )}
      />
    </Grid>
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom color="primary">
        Document Uploads
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload required documents. Supported formats: Images (JPEG, PNG, GIF) and PDF files. Maximum file size: 10MB.
      </Typography>

      <Grid container spacing={3}>
        <UploadField
          name="nicimg"
          label="Upload Staff ID (Image)"
          accept="image/*"
          fileType="image"
          previewUrl={nicPreview}
          currentFile={nicimgFile instanceof File ? nicimgFile : null}
          validationRules={{
            required: "Staff ID is required",
          }}
        />

        <UploadField
          name="passportFile"
          label="Upload Passport (Image)"
          accept="image/*"
          fileType="image"
          previewUrl={passportPreview}
          currentFile={passportFile}
        />

        <UploadField
          name="contractFile"
          label="Upload Contract (PDF)"
          accept="application/pdf"
          fileType="pdf"
          previewUrl={contractPreview}
          currentFile={contractFile}
        />
      </Grid>
    </Box>
  );
}